'use client';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import React, { useState } from 'react';
import { type QuestionImagePickerModel } from 'survey-core';

import { useRouter } from '@/hooks/useRouter';
import { urls } from '@/utils/urls';

import { autoAdvancingAtom } from '../../atoms/autoAdvancingAtom';
import ImagePickerItem from '../imagePickerItem';

const ImagePicker = ({ question }: { question: QuestionImagePickerModel }) => {
  const router = useRouter();
  const [arrowPressed, setArrowPressed] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const [isAutoAdvancing, setIsAutoAdvancing] = useAtom(autoAdvancingAtom);

  // @ts-expect-error - TODO: broken by noImplicitAny
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleOnClick = (choice) => {
    if (question.multiSelect) {
      if (spacePressed) {
        setSpacePressed(false);
        return;
      }

      if (arrowPressed) {
        setArrowPressed(false);
        return;
      }

      if (question.value.includes(choice.value)) {
        question.value.splice(question.value.indexOf(choice.value), 1);
        if (question.value.length === 0) {
          question.value = null;
        } else {
          question.value = [...question.value];
        }
      } else {
        question.value = [...question.value, choice.value];
      }
    } else {
      if (isAutoAdvancing) {
        return;
      }

      question.value = choice.value;

      if (!arrowPressed) {
        if (choice.value === 'improve_hair_growth_primary') {
          setIsAutoAdvancing(true);
          // We can't control for how long the client takes to load the new route so
          // we set an arbitrarily high timeout to stop the button spinner.
          setTimeout(() => {
            setIsAutoAdvancing(false);
          }, 10000);
          return router.push(urls.app.hairRegistration);
        }

        const timeout =
          question.surveyModel.getPropertyValue('autoAdvanceDelay') || 250;
        setIsAutoAdvancing(true);
        setTimeout(() => {
          setIsAutoAdvancing(false);
          question.surveyModel.nextPage() ||
            question.surveyModel.completeLastPage();
        }, timeout);
      }

      setArrowPressed(false);
    }
  };

  // @ts-expect-error - TODO: broken by noImplicitAny
  const handleKeyDownChange = (event, choice) => {
    if (event.key.includes('Arrow')) {
      setArrowPressed(true);
    }

    if (event.key === ' ') {
      setSpacePressed(true);
      if (question.multiSelect) {
        if (question.value.includes(choice.value)) {
          question.value.splice(question.value.indexOf(choice.value), 1);
          if (question.value.length === 0) {
            question.value = null;
          } else {
            question.value = [...question.value];
          }
        } else {
          question.value = [...question.value, choice.value];
        }
        return;
      }
    }
  };

  return (
    <div
      role="presentation"
      className={clsx(
        'cur-text-primary cur-input-imagepicker cur-gap-x-4',
        question.descriptionOrientation === 'horizontal'
          ? 'cur-flex cur-flex-col cur-gap-y-6'
          : 'cur-grid cur-grid-cols-2 cur-gap-y-4',
      )}
    >
      {question.choices.map((choice) => (
        <ImagePickerItem
          key={choice.value}
          question={question}
          choice={choice}
          handleOnClick={handleOnClick}
          handleKeyDownChange={handleKeyDownChange}
        />
      ))}
    </div>
  );
};

export default ImagePicker;
